import { KnowledgeType } from '@uds/reactive-knowledge';
import { AddToBasketResponse } from 'application/actions/domain-search/useAddDomainToBasket';
import { DSTApiResponseExactDomain } from 'presentation/utils/dstApiResponseTypes';

export enum AvailabilityStatus {
  UNVERIFIED = 'UNVERIFIED',
  VERIFYING = 'VERIFYING',
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
  IN_BASKET = 'IN_BASKET',
}

export enum DomainStatus {
  AVAILABLE = 'AVAILABLE',
  VOID = 'VOID',
  TAKEN = 'TAKEN',
  PROMO = 'PROMO',
}

interface GetTotalProps {
  domains: Array<{ price: { display: number }; oldPrice: { display: number } }>;
  prop: 'price' | 'oldPrice';
}

export const getTotal = ({ domains, prop }: GetTotalProps) => {
  let total = 0;
  domains?.forEach((domain) => {
    total += domain[prop].display;
  });
  return total;
};

export const getGodaddyDSTLink = (domain: string, urlQuery: string) => {
  const baseUrl = 'https://www.godaddy.com/domainsearch/find';
  const domainSearched = `?domainToCheck=${domain}`;
  const param = `${domainSearched}&isc=HOSTEUDBS&tmskey=dpp_recom_uk&utm_source=hosteurope&utm_medium=partnerships&utm_campaign=x_dom-broker_partnerships_x_x_invest_fromhosteurope_001`;
  const parkedPageParam = `${domainSearched}&isc=HOSTEUDBS&utm_source=godaddy&utm_medium=parkedpages&utm_campaign=x_dom-broker_parkedpages_x_x_invest_hosteurope_001&tmskey=dpp_dbs`;

  const regexParked = /[?&]fromGDParked/;

  const searchQuery = regexParked.test(urlQuery) ? parkedPageParam : param;

  return `${baseUrl}${searchQuery}`;
};

export const getDomainStatus = (exactMatchProps: DSTApiResponseExactDomain) => {
  if (exactMatchProps == null) {
    return DomainStatus.VOID;
  }

  if (exactMatchProps.is_purchasable === true && exactMatchProps.price != null) {
    if (exactMatchProps.oldPrice != null && exactMatchProps.price.gross < exactMatchProps.oldPrice.gross) {
      return DomainStatus.PROMO;
    }

    return DomainStatus.AVAILABLE;
  }

  if (exactMatchProps.is_purchasable === false && exactMatchProps.price != null) {
    return DomainStatus.TAKEN;
  }

  return DomainStatus.VOID;
};

export interface HandleAddDomainToBasketProps {
  checkAvailability: () => Promise<{ data: { body: boolean } }>;
  setAvailability: (availability: AvailabilityStatus) => void;
  addToBasket: () => Promise<AddToBasketResponse | undefined>;
  showToast: (messageId: string, type?: KnowledgeType) => void;
}

export const handleAddDomainToBasket = async ({
  setAvailability,
  checkAvailability,
  addToBasket,
  showToast,
}: HandleAddDomainToBasketProps) => {
  setAvailability(AvailabilityStatus.VERIFYING);
  let response;
  try {
    response = await checkAvailability();
  } catch (error) {
    showToast('usf.addToBasket.failure', KnowledgeType.ERK);
    setAvailability(AvailabilityStatus.UNVERIFIED);
    return;
  }

  setAvailability(AvailabilityStatus.AVAILABLE);
  if (response?.data.body === true) {
    try {
      const response = await addToBasket();

      if (response == null || response.success !== true) {
        setAvailability(AvailabilityStatus.UNVERIFIED);
        return;
      }
      setAvailability(AvailabilityStatus.IN_BASKET);
    } catch (error) {
      showToast('usf.addToBasket.failure', KnowledgeType.ERK);
      setAvailability(AvailabilityStatus.UNVERIFIED);
    }
  } else {
    showToast('dst.exactMatch.v2.unavailable', KnowledgeType.ERK);
    setAvailability(AvailabilityStatus.UNAVAILABLE);
  }
};

export interface HandleAddMultiDomainToBasketProps {
  checkAvailability: () => Promise<{ data: Array<{ body: boolean } | undefined> } | undefined>;
  setAvailability: (availability: AvailabilityStatus) => void;
  addToBasket: () => Promise<Array<AddToBasketResponse | undefined>>;
  showToast: (messageId: string, type?: KnowledgeType) => void;
}

export const handleAddMultiDomainToBasket = async ({
  setAvailability,
  checkAvailability,
  addToBasket,
  showToast,
}: HandleAddMultiDomainToBasketProps) => {
  setAvailability(AvailabilityStatus.VERIFYING);
  let results;
  try {
    results = await checkAvailability();
  } catch {
    showToast('usf.addToBasket.failure', KnowledgeType.ERK);
    setAvailability(AvailabilityStatus.UNVERIFIED);
    return;
  }

  setAvailability(AvailabilityStatus.AVAILABLE);
  if (results?.data.every((r) => r?.body === true)) {
    try {
      const responses = await addToBasket();

      if (responses == null || responses.some((r) => r == null || r.success !== true)) {
        setAvailability(AvailabilityStatus.UNVERIFIED);
        return;
      }
      setAvailability(AvailabilityStatus.IN_BASKET);
      showToast('usf.addToBasket.success');
    } catch {
      setAvailability(AvailabilityStatus.UNVERIFIED);
      showToast('usf.addToBasket.failure', KnowledgeType.ERK);
    }
  } else {
    showToast('dst.exactMatch.v2.unavailable', KnowledgeType.ERK);
    setAvailability(AvailabilityStatus.UNAVAILABLE);
  }
};

export interface HandleAddTransferDomainToBasketProps {
  setAvailability: (availability: AvailabilityStatus) => void;
  addToBasket: () => Promise<AddToBasketResponse | undefined>;
}

export const handleAddTransferDomainToBasket = async ({
  setAvailability,
  addToBasket,
}: HandleAddTransferDomainToBasketProps) => {
  setAvailability(AvailabilityStatus.VERIFYING);
  try {
    const response = await addToBasket();

    if (response == null || response.success !== true) {
      setAvailability(AvailabilityStatus.UNVERIFIED);
      return;
    }
    setAvailability(AvailabilityStatus.IN_BASKET);
  } catch (error) {
    setAvailability(AvailabilityStatus.UNVERIFIED);
  }
};
